import { useEmbarcacaoVazaoBombas } from "@/modules/embarcacoes";
import type { EmbarcacaoVazaoBombas, EmbarcacaoVazaoBombasField } from "@/modules/embarcacoes";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import {
  DetailField,
  DetailSection,
  DetailSectionFields,
  DetailSectionState,
} from "../detail-section";
import { formatDetailDate, formatDetailNumber } from "../embarcacao-detail-format";
import type { EmbarcacaoDetailSectionProps } from "../embarcacao-detail-section";

export const VAZAO_BOMBAS_ID = "vazao-bombas";
export const VAZAO_BOMBAS_TITLE = "Vazão das Bombas";
export const VAZAO_BOMBAS_LOADING_MESSAGE = "Carregando a Vazão das Bombas da embarcação...";

/** Padrão global de estado vazio do Hub para a seção sem informações (AC3). */
const VAZAO_BOMBAS_EMPTY_STATE: EmptyStateContent = {
  title: "Nenhuma informação de Vazão das Bombas disponível",
  description:
    "Assim que houver dados de vazão e pressão das bombas desta embarcação, eles aparecerão nesta seção.",
};

const TEST_ID = "embarcacao-vazao-bombas";

interface VazaoBombasFieldDefinition {
  readonly key: EmbarcacaoVazaoBombasField;
  /** Rótulo da HU 1735, já com a unidade de medida da planilha (RN001). */
  readonly label: string;
  /** Tratamento do valor textual; hoje só as datas (AC2). */
  readonly format?: (value: string) => string;
}

/**
 * Os 17 campos da seção, na ordem do AC2. O grid de duas colunas os preenche
 * da esquerda para a direita, de modo que cada linha pareia a vazão e a
 * pressão do mesmo produto; a data de atualização da vazão fecha a lista.
 */
export const VAZAO_BOMBAS_FIELDS: VazaoBombasFieldDefinition[] = [
  { key: "vazaoCombustivel", label: "Vazão de Combustível (m³/h)" },
  { key: "pressaoCombustivel", label: "Pressão de Combustível (bar)" },
  { key: "vazaoAgua", label: "Vazão de Água (m³/h)" },
  { key: "pressaoAgua", label: "Pressão de Água (bar)" },
  { key: "vazaoOro", label: "Vazão ORO (m³/h)" },
  { key: "pressaoOro", label: "Pressão ORO (bar)" },
  { key: "vazaoAguaLastro", label: "Vazão de Água de Lastro (m³/h)" },
  { key: "pressaoAguaLastro", label: "Pressão de Água de Lastro (bar)" },
  { key: "vazaoOleoBase", label: "Vazão de Óleo de Base (m³/h)" },
  { key: "pressaoOleoBase", label: "Pressão de Óleo de Base (bar)" },
  { key: "vazaoSalmoura", label: "Vazão de Salmoura (m³/h)" },
  { key: "pressaoSalmoura", label: "Pressão de Salmoura (bar)" },
  { key: "vazaoLama", label: "Vazão de Lama (m³/h)" },
  { key: "pressaoLama", label: "Pressão de Lama (bar)" },
  { key: "vazaoGranel", label: "Vazão de Granel (m³/h)" },
  { key: "pressaoGranel", label: "Pressão de Granel (bar)" },
  { key: "atualizacaoVazaoBomba", label: "Data Atualização Vazão", format: formatDetailDate },
];

/** Valor do campo pronto para a tela; `null` segue como campo sem preenchimento. */
function fieldValue(
  vazaoBombas: EmbarcacaoVazaoBombas,
  { key, format }: VazaoBombasFieldDefinition,
): string | null {
  const value = vazaoBombas[key];
  if (value === null) return null;

  // Vazão e pressão: só o idioma da tela é aplicado, sem cálculo (RN001).
  if (typeof value === "number") return formatDetailNumber(value);

  return format ? format(value) : value;
}

/**
 * Seção Vazão das Bombas do detalhamento da embarcação (US-1798).
 *
 * Estruturada como as demais seções: título, data de última atualização no
 * cabeçalho e uma lista contínua de campos em duas colunas (AC2).
 * Exclusivamente de leitura: nenhum botão ou campo de cadastro, edição ou
 * exclusão (AC5).
 */
export function VazaoBombasSection({
  embarcacaoId,
  onReauthenticate,
}: EmbarcacaoDetailSectionProps) {
  const { status, vazaoBombas, errorMessage, reload } = useEmbarcacaoVazaoBombas(embarcacaoId);

  return (
    <DetailSection
      id={VAZAO_BOMBAS_ID}
      title={VAZAO_BOMBAS_TITLE}
      // Só há data de atualização a exibir quando a seção tem dados.
      ultimaAtualizacao={vazaoBombas ? vazaoBombas.ultimaAtualizacao : undefined}
      testId={TEST_ID}
    >
      {vazaoBombas ? (
        <DetailSectionFields testId={`${TEST_ID}-fields`}>
          {VAZAO_BOMBAS_FIELDS.map((field) => (
            <DetailField
              key={field.key}
              label={field.label}
              value={fieldValue(vazaoBombas, field)}
              startsRow={field.key === "atualizacaoVazaoBomba"}
              testId={`${TEST_ID}-${field.key}`}
            />
          ))}
        </DetailSectionFields>
      ) : (
        <DetailSectionState
          // `success` sempre traz os campos: aqui só chegam carregamento e exceções.
          status={status === "success" ? "error" : status}
          loadingMessage={VAZAO_BOMBAS_LOADING_MESSAGE}
          errorMessage={errorMessage}
          emptyContent={VAZAO_BOMBAS_EMPTY_STATE}
          onReauthenticate={onReauthenticate}
          onRetry={reload}
          testId={TEST_ID}
        />
      )}
    </DetailSection>
  );
}
